// hooks/useConsultantSearch.ts
"use client"

import { useMemo } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useQuery } from "@tanstack/react-query"
import { getConsultants, searchConsultants } from "@/actions/user/user.action"
import { Category, ConsultantFilters } from "@/app/types"

interface ConsultantSearchResult {
  items: any[]
  totalPages: number
}

export const useConsultantSearch = () => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const currentPage = Number(searchParams.get("page") ?? "1")

  const filters = useMemo(() => {
    const professionId = searchParams.get("profession")
    const address = searchParams.get("address")
    const state = searchParams.get("state") ?? undefined
    const city = searchParams.get("city") ?? undefined
    const country = searchParams.get("country") ?? undefined
    const lat = searchParams.get("lat")
    const lng = searchParams.get("lng")

    return {
      profession: professionId ? { id: professionId, name: "" } : undefined,
      location: address && lat && lng
        ? {
          address,
          state,
          city,
          country,
          coordinates: {
            lat: Number(lat),
            lon: Number(lng)
          }
        }
        : undefined,
    }
  }, [searchParams])

  const hasActiveFilters = useMemo(() => {
    return !!filters.profession || !!filters.location
  }, [filters])

  // Featured consultants (when no filters)
  const {
    data: featuredResult = { items: [], totalPages: 0 },
    isLoading: isFeaturedLoading,
    isFetching: isFeaturedFetching,
  } = useQuery<ConsultantSearchResult>({
    queryKey: ["featured-consultants", currentPage],
    queryFn: async () => {
      const res = await getConsultants(currentPage)
      return {
        items: res?.data?.data?.items ?? [],
        totalPages: res?.data?.data?.totalPages ?? 0,
      }
    },
    enabled: !hasActiveFilters,
    staleTime: 1000 * 60 * 5,
  })

  // Filtered consultant search
  const {
    data: searchResult = { items: [], totalPages: 0 },
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    refetch: refetchConsultants,
  } = useQuery<ConsultantSearchResult>({
    queryKey: ["search-consultants", filters, currentPage],
    queryFn: async () => {
      const res = await searchConsultants(filters, currentPage)
      return {
        items: res?.data?.data?.items ?? [],
        totalPages: res?.data?.data?.totalPages ?? 0,
      }
    },
    enabled: hasActiveFilters,
  })

  const executeSearch = (newFilters: ConsultantFilters) => {
    const params = new URLSearchParams()

    if (newFilters.profession?.id) {
      params.set("profession", newFilters.profession.id)
    }

    if (newFilters.location) {
      params.set("address", newFilters.location.address)
      params.set("lat", String(newFilters.location.coordinates.lat))
      params.set("lng", String(newFilters.location.coordinates.lon))
      params.set("country", newFilters.location.country ?? "")
      params.set("state", newFilters.location.state ?? "")
      params.set("city", newFilters.location.city ?? "")
    }

    params.set("page", "1")
    router.push(`${pathname}?${params.toString()}`)
    refetchConsultants()
  }

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set("page", page.toString())
    router.push(`${pathname}?${params.toString()}`)
  }

  const consultants = hasActiveFilters ? searchResult.items : featuredResult.items
  const totalPages = hasActiveFilters ? searchResult.totalPages : featuredResult.totalPages
  const isLoading = isSearchLoading || isFeaturedLoading
  const isFetching = isSearchFetching || isFeaturedFetching

  return {
    consultants,
    totalPages,
    currentPage,
    isLoading,
    isFetching,
    filters,
    hasActiveFilters,
    executeSearch,
    handlePageChange,
  }
}